import type { FC } from 'react'
import { useState, useEffect, useRef, useCallback, useMemo } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faSpinner,
  faExclamationCircle,
  faCheckCircle,
} from '@fortawesome/free-solid-svg-icons'
import { cn } from '@/utils'
import { SavingIndicatorState } from './model/states'

type SavingIndicatorProps = {
  states?: Partial<Record<SavingIndicatorState, boolean>>
  labels?: Partial<Record<SavingIndicatorState, string>>
  className?: string
  timeout?: number
}

// Pre-computed constants to avoid recreation
const DEFAULT_LABELS = {
  [SavingIndicatorState.INITIAL]: '',
  [SavingIndicatorState.SAVING]: 'Saving...',
  [SavingIndicatorState.SAVED]: 'Saved',
  [SavingIndicatorState.ERROR]: 'Failed',
} as const

const STATE_CLASSES = {
  [SavingIndicatorState.INITIAL]: '',
  [SavingIndicatorState.SAVING]: 'text-green-500',
  [SavingIndicatorState.SAVED]: 'text-green-500',
  [SavingIndicatorState.ERROR]: 'text-red-500',
} as const

const STATE_ICONS = {
  [SavingIndicatorState.INITIAL]: null,
  [SavingIndicatorState.SAVING]: faSpinner,
  [SavingIndicatorState.SAVED]: faCheckCircle,
  [SavingIndicatorState.ERROR]: faExclamationCircle,
} as const

const BASE_CLASSES = 'bg-black/40 rounded-sm px-1 text-xs'

export const SavingIndicator: FC<SavingIndicatorProps> = ({
  states,
  labels,
  className,
  timeout = 2000,
}) => {
  const [state, setState] = useState<SavingIndicatorState>(
    SavingIndicatorState.INITIAL,
  )

  const timeoutRef = useRef<NodeJS.Timeout | null>(null)
  const prevStatesRef = useRef(states)

  // Memoize merged labels to prevent recreation on every render
  const mergedLabels = useMemo(() => {
    if (!labels) return DEFAULT_LABELS
    return { ...DEFAULT_LABELS, ...labels }
  }, [labels])

  // Memoize timeout clearing function
  const clearSavedTimeout = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
      timeoutRef.current = null
    }
  }, [])

  // Extract state booleans with memoization to prevent unnecessary effect runs
  const { isSaving, isError, isSaved } = useMemo(() => {
    if (!states) return { isSaving: false, isError: false, isSaved: false }
    return {
      isSaving: Boolean(states[SavingIndicatorState.SAVING]),
      isError: Boolean(states[SavingIndicatorState.ERROR]),
      isSaved: Boolean(states[SavingIndicatorState.SAVED]),
    }
  }, [states])

  // Memoize computed values
  const icon = STATE_ICONS[state]
  const label = mergedLabels[state]
  const stateClass = STATE_CLASSES[state]

  // Pre-compute className to avoid recreation
  const computedClassName = useMemo(
    () => cn(BASE_CLASSES, stateClass, className),
    [stateClass, className],
  )

  // Only run effect when actual state values change, not object reference
  useEffect(() => {
    const prev = prevStatesRef.current
    const prevSaving = prev?.[SavingIndicatorState.SAVING]
    const prevError = prev?.[SavingIndicatorState.ERROR]
    const prevSaved = prev?.[SavingIndicatorState.SAVED]

    // Skip if no actual state changes
    if (
      prevSaving === isSaving &&
      prevError === isError &&
      prevSaved === isSaved
    ) {
      return
    }

    prevStatesRef.current = states

    if (isSaving) {
      setState(SavingIndicatorState.SAVING)
      clearSavedTimeout()
    } else if (isError) {
      setState(SavingIndicatorState.ERROR)
      clearSavedTimeout()
      timeoutRef.current = setTimeout(() => {
        setState(SavingIndicatorState.INITIAL)
      }, timeout)
    } else if (isSaved && state === SavingIndicatorState.SAVING) {
      setState(SavingIndicatorState.SAVED)
      clearSavedTimeout()
      timeoutRef.current = setTimeout(() => {
        setState(SavingIndicatorState.INITIAL)
      }, timeout)
    }
  }, [clearSavedTimeout, isError, isSaved, isSaving, state, states, timeout])

  // Cleanup timeout on unmount
  useEffect(() => {
    return clearSavedTimeout
  }, [clearSavedTimeout])

  // Early return for initial state
  if (state === SavingIndicatorState.INITIAL) {
    return null
  }

  return (
    <div className={computedClassName}>
      {icon && (
        <FontAwesomeIcon
          icon={icon}
          size="sm"
          className={
            state === SavingIndicatorState.SAVING ? 'animate-spin' : undefined
          }
        />
      )}
      {label && <span className="ml-1">{label}</span>}
    </div>
  )
}
